import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { Store } from '@ngrx/store';
import { PermissionDirective, PermissionType } from '@qtek/libs/permissions';
import {
  AppState,
  DocumentTemplatesActions,
  selectTemplatesByType,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { ErrorComponent } from '@qtek/libs/ui';
import { DocumentTemplate, TemplateTypes } from '@qtek/shared/models';
import { Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { createTemplateDialogConfig } from '../create-template-dialog';
import { CreateTemplateDialogComponent } from '../create-template-dialog/create-template-dialog.component';

@Component({
  selector: 'qt-template-selector',
  templateUrl: './template-selector.component.html',
  styleUrls: ['./template-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TemplateSelectorComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    AsyncPipe,
    ReactiveFormsModule,
    TranslationCoreModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatOptionModule,
    FlexLayoutModule,
    ErrorComponent,
    PermissionDirective,
  ],
})
export class TemplateSelectorComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  private destroy$ = new Subject<void>();
  private store = inject(Store<AppState>);
  private dialog = inject(MatDialog);

  templateType = input.required<TemplateTypes>();
  label = input<string>('GNR_TEMPLATE');
  required = input<boolean>(false);
  disabled = input<boolean>(false);

  readonly PermissionType = PermissionType;
  readonly feature = signal<string>('stp/tpls');

  templateControl = new FormControl<DocumentTemplate | null>(null);
  private existingTemplates: string[] = [];

  protected documentTemplates$: Observable<DocumentTemplate[]>;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange = (value: DocumentTemplate | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouchedFn = () => {};
  private isDisabled = false;

  ngOnInit(): void {
    this.documentTemplates$ = this.store
      .select(selectTemplatesByType(this.templateType()))
      .pipe(
        tap(templates => {
          this.existingTemplates = templates.map(template => template.name);
        })
      );

    if (this.required()) {
      this.templateControl.setValidators([Validators.required]);
    }

    this.store.dispatch(
      DocumentTemplatesActions.connectDocumentTemplates({
        payload: { view: 'dtl' },
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  compareTemplates(
    template1: DocumentTemplate | null,
    template2: DocumentTemplate | null
  ): boolean {
    return template1 && template2
      ? template1._id === template2._id
      : template1 === template2;
  }

  openCreateTemplateDialog(): void {
    const dialogConfig = {
      ...createTemplateDialogConfig,
      data: {
        existingTemplates: this.existingTemplates,
        templateType: this.templateType(),
      },
    };
    const dialogRef = this.dialog.open(
      CreateTemplateDialogComponent,
      dialogConfig
    );

    dialogRef
      .afterClosed()
      .pipe(
        filter(result => !!result),
        switchMap(result =>
          this.store.select(selectTemplatesByType(this.templateType())).pipe(
            map(templates =>
              templates.find(template => template._id === result._id)
            ),
            filter(template => !!template),
            take(1)
          )
        ),
        filter(template => !!template),
        takeUntil(this.destroy$)
      )
      .subscribe(template => {
        this.setValue(template);
      });
  }

  getFormControl(): FormControl<DocumentTemplate | null> {
    return this.templateControl;
  }

  setRequired(required: boolean): void {
    if (required) {
      this.templateControl.setValidators([Validators.required]);
    } else {
      this.templateControl.clearValidators();
    }
    this.templateControl.updateValueAndValidity();
  }

  setValue(template: DocumentTemplate | null): void {
    this.templateControl.setValue(template, { emitEvent: false });
    this.onChange(template);
  }

  reset(): void {
    this.templateControl.reset();
    this.templateControl.markAsUntouched();
    this.templateControl.markAsPristine();
  }

  writeValue(value: DocumentTemplate | null): void {
    this.templateControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: DocumentTemplate | null) => void): void {
    this.onChange = fn;
    this.templateControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        fn(value);
      });
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  onTouched(): void {
    this.onTouchedFn();
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.templateControl.disable({ emitEvent: false });
    } else {
      this.templateControl.enable({ emitEvent: false });
    }
  }
}
